/**
 * Create Invitation Modal (External Users)
 *
 * A modal dialog for creating user invitations for external users.
 * Admins can enter an email address, optional display name,
 * and select roles to assign to the invited user.
 */

import React, { useState, useMemo } from 'react';
import { apiRequest } from '../../lib/api';
import { RbacRoleWithPermissions } from '../../lib/types';

interface CreateInvitationModalProps {
  roles: RbacRoleWithPermissions[];
  onClose: () => void;
  onCreated: () => void;
}

export function CreateInvitationModal({ roles, onClose, onCreated }: CreateInvitationModalProps) {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Group roles by category
  const rolesByCategory = useMemo(() => {
    const grouped: Record<string, RbacRoleWithPermissions[]> = {};
    for (const role of roles) {
      const category = role.category || 'Other';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(role);
    }
    // Sort categories: Platform first, then alphabetically, Other last
    const sortedCategories = Object.keys(grouped).sort((a, b) => {
      if (a === 'Platform') return -1;
      if (b === 'Platform') return 1;
      if (a === 'Other') return 1;
      if (b === 'Other') return -1;
      return a.localeCompare(b);
    });
    return { grouped, sortedCategories };
  }, [roles]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || selectedRoles.length === 0) {
      setMessage({ type: 'error', text: 'Email and at least one role are required' });
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await apiRequest('POST', '/api/invitations', {
        email,
        displayName: displayName || undefined,
        roleIds: selectedRoles,
        expirationHours: 168, // 7 days
      });

      if (response.ok) {
        setMessage({ type: 'success', text: `Invitation sent to ${email}` });
        setTimeout(() => {
          onCreated();
          onClose();
        }, 1500);
      } else if (response.status === 409) {
        const error = await response.json();
        if (error.code === 'INVITATION_EXISTS' && error.existingInvitation) {
          const createdDate = new Date(error.existingInvitation.createdAt).toLocaleDateString();
          if (confirm(`An invitation was already sent to ${email} on ${createdDate}. Do you want to revoke it and send a new one?`)) {
            // Revoke the existing invitation
            const revokeResponse = await apiRequest('POST', `/api/invitations/${error.existingInvitation.invitationId}/revoke`);
            if (revokeResponse.ok) {
              // Retry creating the invitation
              const retryResponse = await apiRequest('POST', '/api/invitations', {
                email,
                displayName: displayName || undefined,
                roleIds: selectedRoles,
                expirationHours: 168,
              });
              if (retryResponse.ok) {
                setMessage({ type: 'success', text: `Previous invitation revoked. New invitation sent to ${email}` });
                setTimeout(() => {
                  onCreated();
                  onClose();
                }, 1500);
              } else {
                const retryError = await retryResponse.json();
                setMessage({ type: 'error', text: retryError.message || 'Failed to send new invitation' });
              }
            } else {
              setMessage({ type: 'error', text: 'Failed to revoke existing invitation' });
            }
          } else {
            setMessage({ type: 'error', text: 'Invitation not sent - existing invitation kept' });
          }
        } else {
          setMessage({ type: 'error', text: error.message || 'Invitation already exists' });
        }
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.message || 'Failed to send invitation' });
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: 'Failed to send invitation' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleRole = (roleId: string) => {
    setSelectedRoles(prev =>
      prev.includes(roleId)
        ? prev.filter(id => id !== roleId)
        : [...prev, roleId]
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-card rounded-lg p-6 max-w-2xl w-full shadow-xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4 text-foreground">Invite External User</h2>

        {message && (
          <div className={`mb-4 p-3 rounded-md text-sm ${
            message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
          }`}>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="invite-email" className="block text-sm font-medium text-foreground mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                id="invite-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md shadow-sm bg-background text-foreground focus:ring-primary focus:border-primary"
                placeholder="user@example.com"
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label htmlFor="invite-display-name" className="block text-sm font-medium text-foreground mb-1">
                Display Name
              </label>
              <input
                id="invite-display-name"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md shadow-sm bg-background text-foreground focus:ring-primary focus:border-primary"
                placeholder="John Doe (optional)"
                disabled={isSubmitting}
              />
            </div>
          </div>

          {/* Role Selection - Grouped by Category */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Roles <span className="text-red-500">*</span>
            </label>
            <div className="border border-border rounded-md overflow-hidden">
              {rolesByCategory.sortedCategories.map((category, categoryIndex) => (
                <div key={category} className={categoryIndex > 0 ? 'border-t border-border' : ''}>
                  <div className="bg-muted px-3 py-2 border-b border-border">
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{category}</span>
                  </div>
                  <div className="p-2 grid grid-cols-2 gap-1">
                    {rolesByCategory.grouped[category].map(role => (
                      <label
                        key={role.roleId}
                        className="flex items-start cursor-pointer hover:bg-muted p-2 rounded"
                        title={role.description || undefined}
                      >
                        <input
                          type="checkbox"
                          checked={selectedRoles.includes(role.roleId)}
                          onChange={() => toggleRole(role.roleId)}
                          className="mt-0.5 mr-2 h-4 w-4 text-primary focus:ring-primary border-border rounded"
                          disabled={isSubmitting}
                        />
                        <div>
                          <span className="text-sm text-foreground">{role.name}</span>
                          {role.description && (
                            <p className="text-xs text-muted-foreground mt-0.5">{role.description}</p>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <p className="text-xs text-muted-foreground bg-muted p-2 rounded">
            Invitation expires in 7 days. User will receive an email with a secure link to accept.
          </p>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-foreground bg-card border border-border rounded-md hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Sending...' : 'Send Invitation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
